"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { CircleCheck, Clock, Loader, Mail, Phone, Sparkles } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function StartTheMagic() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll<HTMLElement>();
  return <section data-section-id="87593" id="plan-your-soiree" ref={sectionRef} className="relative w-full bg-background py-24 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-secondary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className={`text-4xl md:text-6xl font-serif font-light text-foreground mb-4 transition-all duration-1000 transform ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            Plan Your <span className="italic text-foreground/80">Soirée</span>
          </h2>
          <p className={`text-lg font-sans font-light text-muted-foreground tracking-wide transition-all duration-1000 delay-200 transform ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            Begin the journey toward an unforgettable gathering. Share your vision with our studio to receive a bespoke curation proposal.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 max-w-6xl mx-auto">
          {}
          <div className={`lg:col-span-8 transition-all duration-1000 delay-300 transform ${sectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <FormProvider formId="echelon_soiree_inquiry_form" sectionName="Plan Your Soiree" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            event_type: "",
            event_date: undefined,
            attendees: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: "Please enter your full name"
            },
            email: {
              required: "Please enter your email address",
              pattern: "email"
            },
            phone: {
              required: "Please enter your phone number",
              pattern: "phone"
            },
            event_type: {
              required: "Please select the occasion type"
            },
            event_date: {
              required: "Please select a preferred date"
            },
            attendees: {
              required: "Please estimate the guest count",
              min: 1
            }
          }} submitText="Send Your Vision" successMessage="Your vision has been received. Our lead curator will contact you within 24 hours to begin the journey." errorMessage="An error occurred during transmission. Please try submitting your vision again.">
              <BookingForm />
            </FormProvider>
          </div>

          {}
          <div className={`lg:col-span-4 transition-all duration-1000 delay-400 transform ${sectionVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="sticky top-24 space-y-6">
              <Card className="border-none shadow-sm bg-card/50 backdrop-blur-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl font-serif font-light tracking-tight">
                    <Sparkles className="w-4 h-4 text-secondary" />
                    The Echelon Process
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full border border-secondary/50 flex items-center justify-center text-[10px] text-foreground font-sans tracking-widest">
                        01
                      </div>
                    </div>
                    <div>
                      <h4 className="font-sans font-medium text-sm uppercase tracking-widest text-foreground">Initial Consultation</h4>
                      <p className="text-sm font-light text-muted-foreground mt-2 leading-relaxed">
                        Our lead curator reviews your details to understand the architectural essence of your vision.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full border border-secondary/50 flex items-center justify-center text-[10px] text-foreground font-sans tracking-widest">
                        02
                      </div>
                    </div>
                    <div>
                      <h4 className="font-sans font-medium text-sm uppercase tracking-widest text-foreground">Curated Concept</h4>
                      <p className="text-sm font-light text-muted-foreground mt-2 leading-relaxed">
                        We develop a preliminary design concept and verify availability for your selected date.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full border border-secondary/50 flex items-center justify-center text-[10px] text-foreground font-sans tracking-widest">
                        03
                      </div>
                    </div>
                    <div>
                      <h4 className="font-sans font-medium text-sm uppercase tracking-widest text-foreground">Seamless Execution</h4>
                      <p className="text-sm font-light text-muted-foreground mt-2 leading-relaxed">
                        You will receive a personalized proposal and coordination timeline within one business day.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-md bg-primary text-primary-foreground rounded-none">
                <CardContent className="p-8 space-y-6">
                  <h3 className="font-serif font-light text-xl tracking-tight">Immediate Inquiry</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 opacity-70" />
                      <Link to="tel:5550109988" className="hover:text-secondary transition-colors font-sans font-light tracking-wide text-sm">
                        +1 (555) 010-9988
                      </Link>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 opacity-70" />
                      <Link to="mailto:concierge@echelonsoiree.com" className="hover:text-secondary transition-colors font-sans font-light tracking-wide text-sm">
                        concierge@echelonsoiree.com
                      </Link>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 opacity-70" />
                      <span className="text-xs font-sans font-light tracking-widest uppercase opacity-70">By Appointment Only</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const eventTypeOptions = ["Private Soirée", "Corporate Gathering", "Wedding Celebration", "Brand Launch", "Intimate Dinner", "Gallery Opening", "Bespoke Celebration"];
  if (isSubmitted && !submitError) {
    return <Card className="border-none shadow-none bg-transparent overflow-hidden">
        <CardContent className="p-12 flex flex-col items-center text-center space-y-6">
          <div className="w-16 h-16 bg-accent/50 rounded-full flex items-center justify-center mb-4">
            <CircleCheck className="w-8 h-8 text-foreground" />
          </div>
          <h3 className="text-3xl font-serif font-light text-foreground tracking-tight">Inquiry Received</h3>
          <p className="text-muted-foreground font-sans font-light max-w-md leading-relaxed">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="mt-8 border-border rounded-none font-sans font-light tracking-widest uppercase text-xs px-8">
            Send Another Inquiry
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="border-none shadow-none bg-transparent overflow-hidden">
      <CardHeader className="bg-transparent border-none pb-12 px-0">
        <CardTitle className="text-3xl font-serif font-light tracking-tight">The Inquiry</CardTitle>
        <CardDescription className="font-sans font-light text-muted-foreground tracking-wide mt-2">
          Please provide the finer details of your upcoming event so we may tailor the experience accordingly.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <Form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 mb-10">
            {}
            <FormField control={control} name="full_name" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-xs uppercase tracking-widest font-medium text-foreground/70">Full Name <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <Input placeholder="Evelyn Thorne" className="border-0 border-b border-border rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary transition-all placeholder:text-muted-foreground/30 font-sans font-light" {...field} />
                  </FormControl>
                  <FormMessage className="text-[10px] font-sans font-light" />
                </FormItem>} />

            {}
            <FormField control={control} name="email" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-xs uppercase tracking-widest font-medium text-foreground/70">Email Address <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="evelyn@example.com" className="border-0 border-b border-border rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary transition-all placeholder:text-muted-foreground/30 font-sans font-light" {...field} />
                  </FormControl>
                  <FormMessage className="text-[10px] font-sans font-light" />
                </FormItem>} />

            {}
            <FormField control={control} name="phone" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-xs uppercase tracking-widest font-medium text-foreground/70">Phone Number <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 000-0000" className="border-0 border-b border-border rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary transition-all placeholder:text-muted-foreground/30 font-sans font-light" {...field} />
                  </FormControl>
                  <FormMessage className="text-[10px] font-sans font-light" />
                </FormItem>} />

            {}
            <FormField control={control} name="event_type" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-xs uppercase tracking-widest font-medium text-foreground/70">Occasion Type <span className="text-destructive">*</span></FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="border-0 border-b border-border rounded-none bg-transparent px-0 focus:ring-0 focus:border-primary transition-all font-sans font-light">
                        <SelectValue placeholder="Select an occasion" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-none border-border">
                      {eventTypeOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i} className="font-sans font-light">
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[10px] font-sans font-light" />
                </FormItem>} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 mb-10">
            {}
            <FormField control={control} name="attendees" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-xs uppercase tracking-widest font-medium text-foreground/70">Guest Count <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <Input type="number" min="1" placeholder="e.g., 40" className="border-0 border-b border-border rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary transition-all placeholder:text-muted-foreground/30 font-sans font-light" {...field} />
                  </FormControl>
                  <FormMessage className="text-[10px] font-sans font-light" />
                </FormItem>} />

            {}
            <FormField control={control} name="event_date" render={({
            field
          }) => <FormItem className="flex flex-col">
                  <FormLabel className="text-xs uppercase tracking-widest font-medium text-foreground/70 mb-2">Desired Event Date <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <div className="border border-border rounded-none p-4 bg-card/30 backdrop-blur-sm shadow-none inline-flex justify-center w-full">
                      <SafeHydrate><Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-none font-sans" /></SafeHydrate>
                    </div>
                  </FormControl>
                  <FormDescription className="text-[10px] font-sans font-light italic mt-2">Select the date you envision for your soirée</FormDescription>
                  <FormMessage className="text-[10px] font-sans font-light" />
                </FormItem>} />
          </div>

          {}
          <div className="mb-12">
            <FormField control={control} name="message" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-xs uppercase tracking-widest font-medium text-foreground/70">Your Vision</FormLabel>
                  <FormControl>
                    <textarea className="flex min-h-[100px] w-full rounded-none border-0 border-b border-border bg-transparent px-0 py-2 text-sm shadow-none placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-none font-sans font-light leading-relaxed" placeholder="Describe the atmosphere, venue, and specific aesthetic requirements..." {...field} />
                  </FormControl>
                  <FormMessage className="text-[10px] font-sans font-light" />
                </FormItem>} />
          </div>

          {submitError && <div className="p-4 mb-8 bg-destructive/5 border border-destructive/10 rounded-none">
              <p className="text-destructive text-xs font-sans font-light tracking-wide">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[240px] h-14 text-xs font-sans font-medium uppercase tracking-[0.2em] rounded-none bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md" size="lg">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-3 h-4 w-4" />
                Transmitting...
              </> : 'Send Your Vision'}
          </Button>
        </Form>
      </CardContent>
    </Card>;
}